import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { APIRoutes } from '../global-misc/api-routes';
import { firstValueFrom } from 'rxjs';
import { CreatePatientModel, UpdatePatientModel, PatientModel, EthnicityType, SexType, PatientPageModel } from '../global-data-models/patient.model';

export enum OrderDir { 
  ASC = "asc", 
  DESC = "desc"
}

export interface PatientPageOptions {
  orgId:      string;
  page:       number;
  orderDir:   OrderDir;
  limit:      number;
  search?:    string;
  orderBy?:   string;
}

export const DEFAULT_PATIENT_PAGE_OPTIONS: PatientPageOptions = {
  orgId:      "NO_ORG_ID",
  page:       1,
  orderDir:   OrderDir.ASC,
  limit:      30,
  orderBy:    "firstName"
}

@Injectable({ providedIn: 'root' })
export class PatientService {
  constructor(private http: HttpClient) {}

  async getEthnicities(): Promise<EthnicityType[]> {
    const apiRoute = APIRoutes.ETHNICITIES_ROUTE;
    return await firstValueFrom(this.http.get<EthnicityType[]>(apiRoute));
  }

  async getSexes(): Promise<SexType[]> {
    const apiRoute = APIRoutes.SEXES_ROUTE;
    return await firstValueFrom(this.http.get<SexType[]>(apiRoute));
  }

  async getPatient(orgId: string, patientId: string): Promise<PatientModel> {
    const apiRoute = APIRoutes.SINGLE_PATIENT_ROUTE.replace(':patientId', patientId).replace(':orgId', orgId);
    return await firstValueFrom(this.http.get<PatientModel>(apiRoute));
  }

  async getPatientsPage(options: PatientPageOptions): Promise<PatientPageModel> {
    const apiRoute = new URL(APIRoutes.PATIENTS_ROUTE.replace(':orgId', options.orgId));

    /* add query params */
    apiRoute.searchParams.append( "page",       String(options.page)      );
    apiRoute.searchParams.append( "orderDir",   String(options.orderDir)  );
    apiRoute.searchParams.append( "limit",      String(options.limit)     );
    if (options.search)   apiRoute.searchParams.append( "search",   String(options.search)  );
    if (options.orderBy)  apiRoute.searchParams.append( "orderBy",  String(options.orderBy) );

    return await firstValueFrom( this.http.get<PatientPageModel>(apiRoute.toString()) );
  }

  async createPatient(orgId: string, patient: CreatePatientModel): Promise<PatientModel> {
    const apiRoute = APIRoutes.PATIENTS_ROUTE.replace(':orgId', patient.org_id);
    return await firstValueFrom(this.http.post<PatientModel>(apiRoute, patient));
  }

  async updatePatient(orgId: string, patientId: string, patient: UpdatePatientModel): Promise<PatientModel> {
    const apiRoute = APIRoutes.SINGLE_PATIENT_ROUTE.replace(':patientId', patientId).replace(':orgId', orgId);
    return await firstValueFrom(this.http.patch<PatientModel>(apiRoute, patient));
  }

  async deletePatient(orgId: string, patientId: string): Promise<void> {
    const apiRoute = APIRoutes.SINGLE_PATIENT_ROUTE.replace(':patientId', patientId).replace(':orgId', orgId);
    return await firstValueFrom(this.http.delete<void>(apiRoute));
  }

  async skipMigration(orgId: string): Promise<void> {
    const apiRoute = APIRoutes.SKIP_MIGRATION_ROUTE.replace(':orgId', orgId);
    return await firstValueFrom(this.http.post<void>(apiRoute, {}));
  }

  async recordMigrationAttempted(orgId: string): Promise<void> {
    const apiRoute = APIRoutes.ATTEMPTED_MIGRATION_ROUTE.replace(':orgId', orgId);
    return await firstValueFrom(this.http.post<void>(apiRoute, {}));
  }

  async migratePatients(orgId: string): Promise<void> {
    const apiRoute = APIRoutes.MIGRATE_PATIENTS_ROUTE.replace(':orgId', orgId);
    return await firstValueFrom(this.http.post<void>(apiRoute, {}));
  }
}
